import React, { memo, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import cx from 'classnames';
import useSWR from 'swr';
import { AxiosResponse } from 'axios';

import { OpportunityCardLink } from 'components/opportunity-page';
import { ButtonV3, FreeText, Stat } from 'components/ComponentV2';
import IconButton from 'components/IconButton';
import Icon, { IconName } from 'components/Icon';
import Link from 'components/Link';
import { BulletList, LinkList } from 'components/ComponentV2/Lists';
import { HuzzleUserAvatar } from 'components/HuzzleUserAvatar';
import { UserActionEnum } from 'hooks/useOpportunity/useOpportunityInternal';
import { TCompany } from 'lib/models/company';
import { parseArrayResponse, parseResponse } from 'lib/utils/parser';
import { API_ROUTES } from 'lib/api-routes';
import { OpportunitiesResponse } from 'lib/models/opportunity';
import { apiInstance } from 'lib/utils/axios';
import { PAGE_ROUTES } from 'lib/page-routes';
import { useOpportunity } from 'lib/providers/OpportunityProvider';
import { TouchpointType } from 'lib/models/touchpoint';
import { UserAvatarFields } from 'lib/models/User.model';
import { isDataAvailable } from 'utils/common.utils';
import { getUserAvatarFields } from 'utils/user';

import styles from './CompanyInfo.module.scss';

type CompanyInfoProps = {
  className?: string;
  company: TCompany;
  opportunityType: TouchpointType;
  showFullContent?: boolean;
};

const CompanyOpportuntiesList = ({
  slug,
  className,
  companyId,
  opportunityType,
}: {
  slug: string;
  className?: string;
  companyId: string;
  opportunityType: TouchpointType;
}) => {
  const { isSearchPage } = useOpportunity();
  const [t] = useTranslation('opportunity');
  const companyOpportunitiesPathname = `${API_ROUTES.COMPANY_LIST}/${slug}/touchpoints?with_details=true&page=1&per_page=3&sort_by=posted_at`;
  const { data: companypportunitiesResponse } = useSWR<AxiosResponse<OpportunitiesResponse>>(companyOpportunitiesPathname, apiInstance.get, {
    revalidateOnFocus: false,
  });
  const opportunities = parseArrayResponse(companypportunitiesResponse?.data);
  if (opportunities.length === 0) {
    return null;
  }
  const similarOpportunitiesPathname = `${PAGE_ROUTES.SEARCH_RESULTS}?type=${opportunityType}&${
    opportunityType === TouchpointType.Event ? 'posted_by_organization_ids' : 'posted_by_company_ids'
  }=${companyId}`;

  return (
    <div className={cx(styles['list-container'], className)}>
      <h3 className={cx(styles.subtitle, styles['opportunities-header'])}>
        {t('company-info.opportunities')}
        {!isSearchPage && (
          <Link href={similarOpportunitiesPathname} className={styles.link}>
            <span>{t('company-info.view-all')}</span>
            <Icon iconName="icon_arrow-right-2" size="small" />
          </Link>
        )}
      </h3>
      <ul className={styles['opportunities-list']}>
        {opportunities.map((opportunity) => (
          <OpportunityCardLink key={opportunity.id} opportunity={opportunity} />
        ))}
      </ul>
    </div>
  );
};

export const CompanyInfo: React.FC<CompanyInfoProps> = memo(({ className, company, showFullContent = true, opportunityType }: CompanyInfoProps) => {
  const [t] = useTranslation('opportunity');
  const { updateUserAction, queryUserAction, queryUserActionId, isFollowLoading } = useOpportunity();
  const {
    id: companyId,
    name,
    slug,
    icon_url,
    website,
    facebook_url,
    twitter_url,
    instagram_url,
    linkedin_url,
    short_description,
    description,
    employees_count,
    founded_year,
    culture_stats,
    benefit_stats,
    following: followingResponse,
    industry: industryResponse,
    insights: insightsResponse,
  } = company;
  const industry = parseResponse(industryResponse);
  const cultureStats = parseArrayResponse(culture_stats);
  const benefitStats = parseArrayResponse(benefit_stats);
  const insights = parseArrayResponse(insightsResponse);
  const followingId = followingResponse ? parseResponse(followingResponse)?.id : null;

  const socialMediaLinks = useMemo(
    () =>
      [
        { icon: 'linkedin', url: linkedin_url },
        { icon: 'facebook', url: facebook_url },
        { icon: 'icon_x', url: twitter_url },
        { icon: 'instagram', url: instagram_url },
      ]
        .filter(({ url }) => url)
        .sort((a, b) => {
          if (a.url && b.url) return 0;
          else if (a.url) return -1;
          else if (b.url) return 1;
          return 0;
        }),
    [facebook_url, twitter_url, instagram_url, linkedin_url]
  );
  const publicURL = PAGE_ROUTES.COMPANY_PUBLIC.replace('[slug]', slug);

  return (
    <div className={cx(styles.container, className)}>
      <div className={styles.header}>
        <div className={styles.top}>
          <HuzzleUserAvatar
            className={styles['img-container']}
            avatarClassName={styles.logo}
            url={publicURL}
            user={getUserAvatarFields({ company }) as UserAvatarFields}
            size="medium"
          />
          <Link href={publicURL} underlineOnHover={false}>
            <h2 className={styles.title}>{name}</h2>
          </Link>
        </div>
        <p className={styles.description}>{short_description}</p>
        <div className={styles.bottom}>
          {isDataAvailable(industry?.name) && <Stat value={industry.name} label={t('company-info.industry')} className={styles.box1} />}
          <div className={styles['box2-content']}>
            {employees_count ? <Stat value={employees_count} label={t('company-info.employees')} className={styles.box2} /> : null}
            {founded_year ? <Stat value={founded_year} label={t('company-info.founded-year')} className={styles.box2} /> : null}
          </div>
        </div>
      </div>
      {showFullContent && (
        <div className={styles.content}>
          {description && (
            <div className={cx(styles['list-container'], styles.list)}>
              <h3 className={styles.subtitle}>{t('company-info.mission-purpose')}</h3>
              <FreeText content={description} />
            </div>
          )}
          <BulletList items={cultureStats} title={t('company-info.culture-values')} className={styles.list} />
          <BulletList items={benefitStats} title={t('company-info.benefits')} className={styles.list} />
          <LinkList items={insights} title={t('company-info.article-news')} className={styles.list} />
          <CompanyOpportuntiesList slug={slug} className={styles.list} companyId={companyId} opportunityType={opportunityType} />
        </div>
      )}
      <footer className={styles.footer}>
        {followingId ? (
          <ButtonV3
            color="secondary"
            startIcon="icon_check-double"
            isFullWidth
            borderless
            onClick={() => updateUserAction({ userAction: UserActionEnum.UNFOLLOW_COMPANY, userActionId: followingId })}
            disabled={isFollowLoading && queryUserAction === UserActionEnum.UNFOLLOW_COMPANY && queryUserActionId === followingId}
          >
            {t('company-info.footer.following')}
          </ButtonV3>
        ) : (
          <ButtonV3
            color="secondary"
            isFullWidth
            borderless
            onClick={() => updateUserAction({ userAction: UserActionEnum.FOLLOW_COMPANY, userActionId: companyId })}
            disabled={isFollowLoading && queryUserAction === UserActionEnum.FOLLOW_COMPANY && queryUserActionId === companyId}
          >
            {t('company-info.footer.follow')}
          </ButtonV3>
        )}
        <Link href={publicURL} underlineOnHover={false}>
          <ButtonV3 color="secondary" isFullWidth borderless>
            {t('company-info.footer.view-profile')}
          </ButtonV3>
        </Link>
        <div className={styles['footer-content']}>
          <a href={website} rel="noreferrer" target="_blank" className={styles.website}>
            <ButtonV3 color="secondary" startIcon="icon_launch" className={cx(styles.btn, styles.bgColor)} borderless>
              {t('company-info.footer.website')}
            </ButtonV3>
          </a>
          {socialMediaLinks.map(({ icon, url }) => (
            <a href={url} key={icon} rel="noreferrer" target="_blank">
              <IconButton iconName={icon as IconName} className={cx(styles.icon, styles.bgColor)} />
            </a>
          ))}
        </div>
      </footer>
    </div>
  );
});

CompanyInfo.displayName = 'CompanyInfo';
